import {type Moment} from 'moment-timezone';
import {type List} from 'immutable';

import {hasValue} from '@famly/stat_ts-utils_has-value';
import type * as Model from 'web-app/react/entities/plans/model';
import type * as ModelSessionBooking from 'web-app/react/entities/common/session-booking';
import type * as ModelProductBooking from 'web-app/react/entities/common/product-booking';
import {type Failable, resolve, fail} from 'web-app/util/failable';

import type * as Local from './plan-types';

/**
 * Transforms an array of plans as defined in `model` to plans suitable for
 * schedule a break.
 *
 * The competing `Model.default.toAPI` function returns an untyped structure
 * that seems to be compatible with the `plans` endpoint but we would prefer
 * to be type strict so changes to `IPlan` hopefully can be caught at compile
 * time.
 *
 * @param plans The plans to transform.
 * @return The transformed plans.
 */
export const transformPlans = (plans: List<Model.Plan>): Failable<Local.Plan[], string[]> => {
    let errors: string[] = [];
    const logError = (msg: string) => {
        errors = [...errors, msg];
    };
    const result = plans.toArray().map(transformPlanWorker(logError));
    if (errors.length > 0) {
        return fail(errors);
    }
    return resolve(result);
};

const transformPlanWorker =
    (logError: (msg: string) => void) =>
    (plan: Model.Plan): Local.Plan => {
        if (!hasValue(plan.childId)) {
            logError('Missing child id in child.');
        }
        if (!hasValue(plan.from)) {
            logError('Missing from date in child.');
        }
        return {
            childId: plan.childId ?? '',
            pricingGroupId: plan.pricingGroupId ?? undefined,
            from: hasValue(plan.from) ? dateToString(plan.from) : '',
            to: hasValue(plan.to) ? dateToString(plan.to) : null,
            planParts: plan.planParts.toArray().map(transformPartWorker(logError)),
            planId: plan.id,
            ruleGroupId: plan.ruleGroupId,
        };
    };

const transformPartWorker =
    (logError: (msg: string) => void) =>
    (part: Model.PlanPart): Local.Part => {
        if (!hasValue(part.termTimeOnly)) {
            logError('Missing term time only from plan part.');
        }
        return {
            ordering: part.ordering,
            billingProfileId: part.billingProfileId ?? undefined,
            sessionBookings: part.sessionBookings.toArray().map(transformSessionBooking),
            productBookings: part.productBookings.toArray().map(transformProductBooking),
            billing: {
                id: part.billing.id,
                invoices: part.billing.invoices,
                weeksOfCare: part.billing.weeksOfCare,
                title: part.billing.title,
            },
            termTimeOnly: hasValue(part.termTimeOnly) ? part.termTimeOnly : false,
            attendanceScheduleId: part.attendanceScheduleId ?? undefined,
            discounts: part.discounts.toArray().map(transformDiscount),
        };
    };

const transformSessionBooking = (booking: ModelSessionBooking.SessionBooking): Local.SessionBooking => {
    return {
        sessionId: booking.sessionId,
        day: booking.day,
        start: booking.start ? timeToString(booking.start) : null,
        end: booking.end ? timeToString(booking.end) : null,
        allowFunding: booking.fundable ?? false,
    };
};

const transformProductBooking = (booking: ModelProductBooking.ProductBooking): Local.ProductBooking => {
    return {
        amount: booking.amount,
        day: booking.day,
        productId: booking.productId,
        bookedPrice: booking.bookedPrice ? booking.bookedPrice.toString() : null,
        planRuleId: booking.planRuleId,
    };
};

const transformDiscount = (discount: Model.Discount): Local.Discount => {
    return {
        amount: discount.amount,
        isPercent: discount.isPercent,
        ordering: discount.ordering,
        showOnInvoice: discount.showOnInvoice,
        title: discount.title,
        discountPresetId: discount.discountPresetId,
        period: discount.period,
    };
};
const dateToString = (moment: Moment): string => moment.format('YYYY-MM-DD');
const timeToString = (moment: Moment): string => moment.format('HH:mm');
